import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FilePenLine,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import api from "../api/api";
import { useDialog } from "../components/DialogProvider";
import { EmptyState, LoadingCard, PremiumCard, StatusBadge } from "../components/ui";
import { formatDateTime } from "../utils/datetime";

const PAGE_SIZE = 20;
const STATUS_TABS = [
  ["PENDING", "Pending"],
  ["APPROVED", "Approved"],
  ["REJECTED", "Rejected"],
  ["ALL", "Semua"],
];

function statusTone(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return "emerald";
  if (s === "REJECTED") return "rose";
  if (s === "PENDING") return "amber";
  return "slate";
}

function statusLabel(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return "Disetujui";
  if (s === "REJECTED") return "Ditolak";
  if (s === "PENDING") return "Menunggu";
  return status || "-";
}

function moduleLabel(module) {
  if (module === "stock_in") return "Stock Masuk";
  if (module === "stock_out") return "Stock Keluar";
  return module || "-";
}

function actionLabel(action) {
  if (action === "EDIT") return "Edit";
  if (action === "DELETE") return "Delete";
  return action || "-";
}

function actionIcon(action) {
  if (action === "DELETE") return Trash2;
  return FilePenLine;
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function fieldRows(row) {
  const before = row.old_data || {};
  const after = row.new_data || {};
  const keys = [
    ["kode_barang", "Kode Barang"],
    ["qty", "Qty"],
    ["keterangan", "Keterangan"],
    ["tanggal", "Tanggal"],
    ["tujuan", "Tujuan"],
    ["source", "Source"],
  ];

  return keys
    .filter(([key]) => before[key] != null || after[key] != null)
    .map(([key, label]) => {
      const beforeValue = displayValue(before[key]);
      const afterValue = row.action_type === "DELETE" ? "-" : displayValue(after[key]);
      return {
        key,
        label,
        beforeValue,
        afterValue,
        changed: row.action_type === "DELETE" || beforeValue !== afterValue,
      };
    });
}

function qtyDelta(row) {
  if (row.action_type === "DELETE") return "hapus";
  const before = Number(row.old_data?.qty);
  const after = Number(row.new_data?.qty);
  if (!Number.isFinite(before) || !Number.isFinite(after)) return "-";
  const delta = after - before;
  if (delta === 0) return "0";
  return delta > 0 ? `+${delta}` : String(delta);
}

function qtyDeltaClass(delta) {
  const value = String(delta || "");
  if (value.startsWith("+")) return "text-emerald-700";
  if (value.startsWith("-") || value === "hapus") return "text-rose-700";
  return "text-slate-700";
}

function requestTitle(row) {
  return row.old_data?.kode_barang || row.new_data?.kode_barang || `Target #${row.target_id}`;
}

function normalizeRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

function buildCounts(rows) {
  const next = { PENDING: 0, APPROVED: 0, REJECTED: 0, ALL: 0 };
  (rows || []).forEach((row) => {
    const key = String(row?.status || "").toUpperCase();
    if (Object.prototype.hasOwnProperty.call(next, key)) next[key] += 1;
    next.ALL += 1;
  });
  return next;
}

function FieldChangeTable({ row }) {
  const rows = fieldRows(row);

  if (!rows.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Tidak ada field yang bisa dibandingkan.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-[640px] w-full text-sm">
        <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">Field</th>
            <th className="px-3 py-2 text-left">Sebelum</th>
            <th className="px-3 py-2 text-left">{row.action_type === "DELETE" ? "Sesudah" : "Sesudah Request"}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((item) => (
            <tr key={item.key} className={item.changed ? "bg-amber-50/50" : ""}>
              <td className="px-3 py-2 font-semibold text-slate-700">{item.label}</td>
              <td className="px-3 py-2 break-words text-slate-900">{item.beforeValue}</td>
              <td className={"px-3 py-2 break-words " + (item.changed ? "font-semibold text-emerald-700" : "text-slate-700")}>
                {item.afterValue}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountStrip({ counts }) {
  const items = [
    ["PENDING", "Pending", "Menunggu keputusan", "amber"],
    ["APPROVED", "Approved", "Sudah diterapkan", "emerald"],
    ["REJECTED", "Rejected", "Ditolak admin", "rose"],
    ["ALL", "Total", "Semua request", "slate"],
  ];

  return (
    <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
      {items.map(([key, label, hint, tone]) => (
        <div key={key} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
              <div className="mt-1 text-[11px] text-slate-500">{hint}</div>
            </div>
            <div className="text-xl font-semibold tabular-nums text-slate-950">{counts?.[key] ?? 0}</div>
          </div>
          <div className="mt-2">
            <StatusBadge tone={tone}>{key === "ALL" ? "Inventory integrity" : statusLabel(key)}</StatusBadge>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApprovalToolbar({ statusFilter, counts, rangeText, loading, onStatusChange, onRefresh }) {
  return (
    <div className="sticky top-[73px] z-20 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-2 shadow-sm backdrop-blur-sm">
      <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {STATUS_TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => onStatusChange(value)}
              className={
                "inline-flex min-h-10 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors " +
                (statusFilter === value
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50")
              }
            >
              <span>{label}</span>
              <span className={statusFilter === value ? "rounded-full bg-white/15 px-2 py-0.5 text-xs" : "rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"}>
                {counts?.[value] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 xl:justify-end">
          <div className="text-sm font-medium text-slate-500">{rangeText}</div>
          <button type="button" onClick={onRefresh} disabled={loading} className="btn btn-secondary min-h-10 px-3 py-2">
            <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovalRow({ row, expanded, processing, onToggle, onDecide }) {
  const isPending = String(row.status || "").toUpperCase() === "PENDING";
  const delta = qtyDelta(row);
  const deltaClass = qtyDeltaClass(delta);
  const ActionIcon = actionIcon(row.action_type);

  return (
    <PremiumCard className={"p-0 " + (isPending ? "border-amber-200" : "")}>
      <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-[minmax(260px,1.5fr)_140px_130px_130px_180px_auto] lg:items-center lg:p-4">
        <button type="button" onClick={onToggle} className="min-w-0 rounded-lg text-left transition-colors hover:bg-slate-50 lg:hover:bg-transparent">
          <div className="flex items-start gap-3">
            <div className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border " + (row.action_type === "DELETE" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-sky-200 bg-sky-50 text-sky-700")}>
              <ActionIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={statusTone(row.status)}>{statusLabel(row.status)}</StatusBadge>
                <StatusBadge tone="slate">{moduleLabel(row.module)}</StatusBadge>
              </div>
              <div className="mt-2 flex min-w-0 items-center gap-2">
                <ChevronDown className={"h-4 w-4 shrink-0 text-slate-400 transition-transform " + (expanded ? "rotate-180" : "")} />
                <div className="truncate text-base font-semibold text-slate-950">{requestTitle(row)}</div>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500">
                <span>Request #{row.id}</span>
                <span className="hidden sm:inline">/</span>
                <span>Target #{row.target_id}</span>
                <span className="hidden sm:inline">/</span>
                <span>{actionLabel(row.action_type)}</span>
              </div>
            </div>
          </div>
        </button>
        

        <div className="rounded-lg bg-slate-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-slate-400 lg:hidden">
            <UserRound className="h-3.5 w-3.5" />
            Requester
          </div>
          <div className="mt-1 truncate text-sm font-semibold text-slate-800 lg:mt-0">{row.requester_name || `User #${row.requested_by}`}</div>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 px-3 py-2 lg:block lg:bg-transparent lg:px-0 lg:py-0">
          <div className="text-[11px] font-semibold uppercase text-slate-400 lg:hidden">Qty</div>
          <div className="text-right text-sm text-slate-600 lg:text-left">
            {displayValue(row.old_data?.qty)}
            <span className="mx-1 text-slate-400">to</span>
            <span className="font-semibold text-slate-900">{row.action_type === "DELETE" ? "-" : displayValue(row.new_data?.qty)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 px-3 py-2 lg:block lg:bg-transparent lg:px-0 lg:py-0">
          <div className="text-[11px] font-semibold uppercase text-slate-400 lg:hidden">Delta</div>
          <div className={"text-right text-sm font-semibold lg:text-left " + deltaClass}>{delta}</div>
        </div>

        <div className="rounded-lg bg-slate-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-slate-400 lg:hidden">
            <Clock3 className="h-3.5 w-3.5" />
            Tanggal
          </div>
          <div className="mt-1 text-sm text-slate-600 lg:mt-0">{formatDateTime(row.created_at) || "-"}</div>
        </div>

        {isPending ? (
          <div className="grid grid-cols-2 gap-2 lg:flex lg:justify-end">
            <button
              type="button"
              disabled={processing}
              onClick={() => onDecide(row, "reject")}
              className="btn btn-secondary min-h-10 px-3 py-2"
            >
              <X className="h-4 w-4" />
              Tolak
            </button>
            <button
              type="button"
              disabled={processing}
              onClick={() => onDecide(row, "approve")}
              className="btn btn-primary min-h-10 px-3 py-2"
            >
              <Check className="h-4 w-4" />
              {processing ? "Proses" : "Setujui"}
            </button>
          </div>
        ) : (
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-left text-sm text-slate-500 lg:bg-transparent lg:px-0 lg:py-0 lg:text-right">
            {row.approved_by_name || row.approver_name || "-"}
          </div>
        )}
      </div>

      {expanded ? (
        <div className="border-t border-slate-100 bg-slate-50/70 p-3 lg:p-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <FieldChangeTable row={row} />

            <div className="grid gap-3">
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Alasan</div>
                <div className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-800">{row.reason || "-"}</div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500">Diproses oleh</span>
                  <span className="font-semibold text-slate-900">{row.approved_by_name || row.approver_name || "-"}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-slate-500">Waktu proses</span>
                  <span className="font-semibold text-slate-900">{formatDateTime(row.approved_at) || "-"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PremiumCard>
  );
}

export default function AdminApproval() {
  const dialog = useDialog();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0, ALL: 0 });
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(0);
  const [expandedId, setExpandedId] = useState(0);
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    const activeParams = new URLSearchParams({
      status: statusFilter,
      limit: String(PAGE_SIZE),
      offset: String((page - 1) * PAGE_SIZE),
    });
    const countParams = new URLSearchParams({
      status: "ALL",
      limit: "300",
      offset: "0",
    });

    try {
      setLoading(true);
      const [activeRes, countRes] = await Promise.all([
        api.get(`/approval?${activeParams.toString()}`),
        api.get(`/approval?${countParams.toString()}`),
      ]);
      const nextRows = normalizeRows(activeRes.data);
      const allRows = normalizeRows(countRes.data);
      const nextCounts = buildCounts(allRows);
      setRows(nextRows);
      setCounts(nextCounts);
      setTotal(nextCounts[statusFilter] ?? nextRows.length);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal memuat approval request");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil((Number(total) || 0) / PAGE_SIZE)), [total]);
  const rangeText = useMemo(() => {
    if (!total) return "0 request";
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(total, page * PAGE_SIZE);
    return `${start}-${end} dari ${total} request`;
  }, [page, total]);

  const changeStatus = (value) => {
    setStatusFilter(value);
    setPage(1);
    setExpandedId(0);
  };

  const decide = useCallback(async (row, action) => {
    const approve = action === "approve";
    const ok = await dialog.confirm(
      `${approve ? "Setujui" : "Tolak"} request ${actionLabel(row.action_type)} ${moduleLabel(row.module)} #${row.target_id}?`,
      {
        title: approve ? "Setujui Request" : "Tolak Request",
        confirmText: approve ? "Setujui" : "Tolak",
        cancelText: "Batal",
      }
    );
    if (!ok) return;

    try {
      setProcessingId(row.id);
      await dialog.toastPromise(
        api.post(`/approval/${row.id}/${action}`),
        {
          loading: approve ? "Menerapkan perubahan..." : "Menolak request...",
          success: approve
            ? "Request disetujui dan perubahan stock sudah diterapkan."
            : "Request ditolak. Stock tidak berubah.",
          error: (e) => e?.response?.data?.message || e.message || "Gagal memproses request.",
        },
        { successTitle: "Selesai", errorTitle: "Gagal" }
      );
      setExpandedId(0);
      await fetchRows();
    } finally {
      setProcessingId(0);
    }
  }, [dialog, fetchRows]);

  return (
    <div className="space-y-4 animate-fade-in">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:p-5">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              Inventory Integrity
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 md:text-3xl">Approval Request</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Review request edit dan hapus histori stock sebelum perubahan diterapkan. Fokus utama ada di request pending.
            </p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="text-xs font-semibold uppercase text-amber-700">Butuh keputusan</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{counts?.PENDING ?? 0}</div>
          </div>
        </div>
      </section>

      <CountStrip counts={counts} />

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

      <ApprovalToolbar
        statusFilter={statusFilter}
        counts={counts}
        rangeText={rangeText}
        loading={loading}
        onStatusChange={changeStatus}
        onRefresh={fetchRows}
      />

      {loading && rows.length === 0 ? (
        <LoadingCard label="Memuat approval request..." />
      ) : rows.length === 0 ? (
        <EmptyState title="Tidak ada request" description="Request edit/hapus dari staff akan tampil di sini." />
      ) : (
        <div className="grid gap-3">
          <div className="hidden grid-cols-[minmax(260px,1.5fr)_140px_130px_130px_180px_auto] gap-3 px-4 text-xs font-semibold uppercase text-slate-400 lg:grid">
            <div>Request</div>
            <div>Requester</div>
            <div>Qty</div>
            <div>Delta</div>
            <div>Tanggal</div>
            <div className="text-right">Aksi / Processed</div>
          </div>

          {rows.map((row) => (
            <ApprovalRow
              key={row.id}
              row={row}
              expanded={expandedId === row.id}
              processing={processingId === row.id}
              onToggle={() => setExpandedId((current) => (current === row.id ? 0 : row.id))}
              onDecide={decide}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="text-slate-500">
          Halaman {page} dari {pageCount}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <button
            type="button"
            disabled={loading || page <= 1}
            onClick={() => {
              setPage((p) => Math.max(1, p - 1));
              setExpandedId(0);
            }}
            className="btn btn-secondary min-h-10 px-3 py-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Sebelumnya
          </button>
          <button
            type="button"
            disabled={loading || page >= pageCount}
            onClick={() => {
              setPage((p) => Math.min(pageCount, p + 1));
              setExpandedId(0);
            }}
            className="btn btn-secondary min-h-10 px-3 py-2"
          >
            Berikutnya
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
